import { api, apiCall } from './client'
import type { ApiResponse, FileMetadata, PaginatedData } from '../../types'

export type FileListParams = {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  patientId?: number
  type?: string
}

export interface CreateFileData {
  name: string
  type: string
  mimeType?: string
  data: string | ArrayBuffer
  size: number
  patientId?: number
  description?: string
}

export type UpdateFileData = Partial<CreateFileData>

export const filesService = {
  async list(
    params?: FileListParams
  ): Promise<ApiResponse<PaginatedData<FileMetadata>>> {
    return apiCall(
      api.get<ApiResponse<PaginatedData<FileMetadata>>>('/files', { params })
    )
  },

  async getById(id: number): Promise<ApiResponse<FileMetadata>> {
    return apiCall(api.get<ApiResponse<FileMetadata>>(`/files/${id}`))
  },

  async getByPatientId(
    patientId: number,
    params?: Omit<FileListParams, 'patientId'>
  ): Promise<ApiResponse<PaginatedData<FileMetadata>>> {
    return apiCall(
      api.get<ApiResponse<PaginatedData<FileMetadata>>>(
        `/files/patient/${patientId}`,
        { params }
      )
    )
  },

  async create(fileData: CreateFileData): Promise<ApiResponse<FileMetadata>> {
    return apiCall(api.post<ApiResponse<FileMetadata>>('/files', fileData))
  },

  async update(
    id: number,
    fileData: UpdateFileData
  ): Promise<ApiResponse<FileMetadata>> {
    return apiCall(api.put<ApiResponse<FileMetadata>>(`/files/${id}`, fileData))
  },

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiCall(api.delete<ApiResponse<void>>(`/files/${id}`))
  },

  async download(id: number): Promise<Blob> {
    const response = await api.get(`/files/${id}/download`, {
      responseType: 'blob',
    })
    return response.data
  },
}

export type FilesService = typeof filesService
